// types
import {
  IPlan,
  IField,
  IReview,
  IPlanKpi,
  IPlanAction,
  IPlanObjective,
  IScheduledReview,
  IFieldValueMapItem,
  ScheduledReviewEndsOn,
  ScheduledReviewPeriodEndsOn,
  ScheduledReviewRepeatEveryType
} from 'types/graphql'
// types
import { IOption } from 'components/FormElements/Select'
// constants
import { ENTITIES, OBJECT_ENTITY_NAMES } from 'constants/index'
import { REVIEW_REPEAT_VALUES, REVIEW_STATUSES, REVIEW_TYPE_VALUES } from 'constants/review'
// graphql
import { PLAN_OBJECTIVE_DETAILS_QUERY } from 'graphql/planObjectives/queries'
import { PLAN_ACTION_DETAILS_QUERY } from 'graphql/planActions/queries'
import { PLAN_KPI_DETAILS_QUERY } from 'graphql/planKPIs/queries'
import { PLANS_DETAILS_QUERY } from 'graphql/plan/queries'
// utils
import filter from 'lodash/fp/filter'
import first from 'lodash/first'
import flatten from 'lodash/flatten'
import get from 'lodash/get'
import isEmpty from 'lodash/isEmpty'
import map from 'lodash/fp/map'
import moment from 'moment'
import type { Moment } from 'moment'
import orderBy from 'lodash/fp/orderBy'
import pipe from 'lodash/fp/pipe'
import { safeGetOr } from 'utils/fp'

export const generateReviews = ({
  entity
}: {
  entity: (IPlan | IPlanObjective | IPlanAction | IPlanKpi) & any
}): any[] => {
  const scheduledReviews = safeGetOr<any, any, any>([], 'scheduledReviews')(entity)

  const singleScheduledReviews = scheduledReviews.filter(({ repeating }) => !repeating)

  const singleReviewsToBeCreated = singleScheduledReviews.map((scheduledReview) => ({
    startPeriod: moment(scheduledReview.startPeriod),
    endPeriod: moment(scheduledReview.endPeriod),
    dueDate: getReviewDueDate({ scheduledReview }),
    scheduledReview
  }))

  const createdSingleReviews = pipe<any, any, any, any>(
    safeGetOr([], 'reviews'),
    filter(
      ({ scheduledReview, endPeriodAt, id }) =>
        !scheduledReview.repeating &&
        endPeriodAt &&
        !singleReviewsToBeCreated.some((generatedReview: IReview) => generatedReview.id === id)
    ),
    map((review: any) => ({
      ...review,
      startPeriod: moment(review.startPeriod),
      endPeriod: moment(review.endPeriodAt),
      dueDate: getReviewDueDate(review)
    }))
  )(entity)

  const filteredSingleScheduled = singleReviewsToBeCreated.filter(
    ({ scheduledReview, dueDate: scheduledReviewDue }) => {
      const reviews = createdSingleReviews.filter(({ scheduledReviewId }) => scheduledReviewId === scheduledReview.id)

      if (!isEmpty(reviews)) {
        return !reviews.some(
          ({ startPeriod, endPeriod, dueDate }) =>
            startPeriod.isSame(scheduledReview.startPeriod, 'd') &&
            endPeriod.isSame(scheduledReview.endPeriod, 'd') &&
            dueDate.isSame(scheduledReviewDue, 'd')
        )
      } else {
        return true
      }
    }
  )

  const repeatingScheduledReviews = scheduledReviews.filter(({ repeating }) => repeating)

  const generatedReports = flatten(
    repeatingScheduledReviews.map((scheduledReview) => generateRepeatingReviews({ scheduledReview, entity }))
  )

  const createdRepeatingReviews = pipe<any, any, any, any>(
    safeGetOr([], 'reviews'),
    filter(
      ({ scheduledReview, endPeriodAt, id }) =>
        scheduledReview.repeating &&
        endPeriodAt &&
        !generatedReports.some((generatedReview: IReview) => generatedReview.id === id)
    ),
    map((review: IReview) => {
      const endPeriod = moment(review.endPeriodAt)

      return {
        ...review,
        endPeriod,
        startPeriod: moment(review.startPeriod),
        dueDate: getReviewDueDate({ ...review, endPeriod })
      }
    })
  )(entity)

  return orderBy(
    (review) => moment(review.startPeriod).valueOf(),
    ['desc'],
    [...createdSingleReviews, ...filteredSingleScheduled, ...generatedReports, ...createdRepeatingReviews]
  )
}

export const generateRepeatingReviews = ({
  scheduledReview,
  entity
}: {
  scheduledReview: IScheduledReview
  entity: (IPlan | IPlanObjective | IPlanAction | IPlanKpi) & any
}) => {
  const periodStart = moment(scheduledReview.startPeriod).startOf('day')
  const today = moment().startOf('day')
  const endsOn = scheduledReview.endsOn === ScheduledReviewEndsOn.On && moment(scheduledReview.endsOnDate)

  const isEndsOnExceeded = (date: Moment): boolean => !!endsOn && date.isAfter(endsOn)

  if (!scheduledReview.repeating) {
    return []
  }

  const calculatedReviews: any[] = []
  const doesEndOnLastDay = scheduledReview.periodEndsOn === ScheduledReviewPeriodEndsOn.LastDay

  const period = scheduledReview.repeatEveryType === ScheduledReviewRepeatEveryType.Monthly ? 'month' : 'week'
  const isoPeriod = scheduledReview.repeatEveryType === ScheduledReviewRepeatEveryType.Monthly ? 'month' : 'isoWeek'

  const start = periodStart.clone()
  let shouldCalculate = true
  let isFutureLimitReached

  const reviews = (entity.reviews || []).filter(({ scheduledReviewId }) => scheduledReviewId === scheduledReview.id)

  do {
    isFutureLimitReached = start.isSameOrAfter(today, 'day')

    const endPeriod = start.clone()

    if (doesEndOnLastDay) {
      endPeriod.add(scheduledReview.repeatEveryValue - 1, period).endOf(isoPeriod)
    } else {
      endPeriod.add(scheduledReview.repeatEveryValue, period).subtract(1, 'day')
    }

    const currentPeriodReview = reviews.find((review) =>
      moment(review.startPeriod).startOf('day').isSame(start.startOf('day'))
    )

    if (!isEndsOnExceeded(start) && !isEndsOnExceeded(endPeriod)) {
      calculatedReviews.push({
        scheduledReview,
        ...currentPeriodReview,
        startPeriod: start.clone(),
        endPeriod: endPeriod.clone(),
        dueDate: getReviewDueDate({ scheduledReview, endPeriod })
      })

      start.add(scheduledReview.repeatEveryValue, period)

      if (doesEndOnLastDay) {
        start.startOf(isoPeriod)
      }

      if (isFutureLimitReached) {
        shouldCalculate = false
      }
    } else {
      shouldCalculate = false
    }
  } while (shouldCalculate)

  return calculatedReviews
}

const calculateReviewEndPeriod = (scheduledReview) => {
  const periodStart = moment(scheduledReview.startPeriod).startOf('day')
  const period = scheduledReview.repeatEveryType === ScheduledReviewRepeatEveryType.Monthly ? 'month' : 'week'
  const isoPeriod = scheduledReview.repeatEveryType === ScheduledReviewRepeatEveryType.Monthly ? 'month' : 'isoWeek'
  const doesEndOnLastDay = scheduledReview.periodEndsOn === ScheduledReviewPeriodEndsOn.LastDay

  if (doesEndOnLastDay) {
    return periodStart.add(scheduledReview.repeatEveryValue - 1, period).endOf(isoPeriod)
  } else {
    return periodStart.add(scheduledReview.repeatEveryValue, period).subtract(1, 'day')
  }
}

export const getRepeatingReviewDueDate = (data) => {
  const { endPeriod, end, value, period } = data

  let normalizedEndPeriod = endPeriod

  if (typeof endPeriod === 'string') {
    normalizedEndPeriod = moment(endPeriod)
  }

  const subtractDate = (period) => {
    return moment(normalizedEndPeriod.clone().subtract(value, period))
  }

  const addDate = (period) => {
    return moment(normalizedEndPeriod.clone().add(value, period))
  }

  if (end === REVIEW_REPEAT_VALUES.END) return moment(normalizedEndPeriod)

  if (end === REVIEW_REPEAT_VALUES.BEFORE) {
    if (period === REVIEW_TYPE_VALUES.DAYS) return subtractDate('day')
    if (period === REVIEW_TYPE_VALUES.WEEKS) return subtractDate('week')
    if (period === REVIEW_TYPE_VALUES.MONTHS) return subtractDate('month')
  }

  if (end === REVIEW_REPEAT_VALUES.AFTER) {
    if (period === REVIEW_TYPE_VALUES.DAYS) return addDate('day')
    if (period === REVIEW_TYPE_VALUES.WEEKS) return addDate('week')
    if (period === REVIEW_TYPE_VALUES.MONTHS) return addDate('month')
  }
}

const getReviewDueDate = (review) => {
  const isReviewRepeating = get(review, 'scheduledReview.repeating')

  if (isReviewRepeating) {
    const endPeriod = get(review, 'endPeriod')
    const reviewDate = get(review, 'scheduledReview.scheduledReviewers[0].reviewDate')

    const { end, value, period } = reviewDate

    return getRepeatingReviewDueDate({ endPeriod, end, value, period })
  } else {
    return review.dueAt
      ? moment(review.dueAt)
      : moment(get(review, 'scheduledReview.scheduledReviewers[0].reviewDate.date'))
  }
}

export const getClosestReview: any = (entity, filteredReviews): IReview & any => {
  const generatedReviews: IReview & any = orderBy(
    (review: IReview & any) => review.dueDate.valueOf(),
    'asc',
    generateReviews({ entity })
  )

  const reviews = entity ? generatedReviews : filteredReviews

  const overdueReviews = pipe(
    filter((review: IReview & any) => review.dueDate.isBefore(moment(), 'day')),
    filter((review) => review.status !== REVIEW_STATUSES.COMPLETE)
  )(reviews)

  const todayOrFutureReviews = pipe(
    filter((review: IReview & any) => review.status !== REVIEW_STATUSES.COMPLETE),
    filter((review) => review.dueDate.isSameOrAfter(moment(), 'day'))
  )(reviews)

  const completedReviews = pipe(
    filter((review: IReview & any) => review.status === REVIEW_STATUSES.COMPLETE),
    filter((review: IReview & any) => review.endPeriod.isAfter(moment(), 'day'))
  )(reviews)

  let closestReview

  if (!isEmpty(overdueReviews)) {
    closestReview = first(overdueReviews)
  }

  if (isEmpty(overdueReviews) && !isEmpty(todayOrFutureReviews)) {
    closestReview = first(todayOrFutureReviews)
  }

  if (isEmpty(overdueReviews) && isEmpty(todayOrFutureReviews) && !isEmpty(completedReviews)) {
    closestReview = first(completedReviews)
  }

  if (!closestReview) {
    return null
  }

  return closestReview
}

export const getEntityDescription = (entity) => {
  const entityType = get(entity, 'entityType', '')

  switch (entityType) {
    case ENTITIES.PLAN_KPI:
      return OBJECT_ENTITY_NAMES.KPI
    case ENTITIES.PLAN:
      return OBJECT_ENTITY_NAMES.PLAN
    case ENTITIES.PLAN_OBJECTIVE:
      return ''
    case ENTITIES.PLAN_ACTION:
      return OBJECT_ENTITY_NAMES.ACTION
  }
}

export const getRefetchQueriesForReviews = (entity) => {
  let refetchQueries = []

  const getQueryForRefetch = () => {
    if (entity.entityType === ENTITIES.PLAN_OBJECTIVE) {
      return PLAN_OBJECTIVE_DETAILS_QUERY
    }
    if (entity.entityType === ENTITIES.PLAN_ACTION) {
      return PLAN_ACTION_DETAILS_QUERY
    }
    if (entity.entityType === ENTITIES.PLAN_KPI) {
      return PLAN_KPI_DETAILS_QUERY
    }
  }

  if (entity.entityType === ENTITIES.PLAN) {
    refetchQueries = [
      {
        query: PLANS_DETAILS_QUERY,
        variables: {
          filter: {
            id: entity.id
          }
        }
      }
    ]
  } else {
    refetchQueries = [
      {
        query: getQueryForRefetch(),
        variables: {
          filter: {
            ids: [entity.id]
          }
        }
      }
    ]
  }

  return refetchQueries
}

// review.fieldValueMap ([{ fieldId, fieldValue }]) → keyed form values
// { [fieldId]: { label, value } | null } for single, { [fieldId]: [{ label, value }] } for multiple
export const mapReviewPicklistsToFormValues = (fields: IField[] = [], review: IReview) => {
  const fieldValueMap = review?.fieldValueMap || []

  return fields.reduce((acc, field) => {
    const selected = fieldValueMap.find((item) => item?.fieldId === field.id)

    const toOption = (id) => {
      const option = (field.options || []).find((item) => item.id === id)

      return option ? { label: option.label, value: option.id } : null
    }

    if (field.multiple) {
      const ids = Array.isArray(selected?.fieldValue) ? selected.fieldValue : []

      acc[field.id] = ids.map(toOption).filter(Boolean)
    } else {
      acc[field.id] = toOption(selected?.fieldValue)
    }

    return acc
  }, {})
}

// keyed form values → fieldValueUpsert input (value: optionId | optionId[])
// on update we send fieldValueId of the existing value (update in place); on create — fieldId (create new)
export const mapFormPicklistsToFieldValues = (data, fieldValueMap: IFieldValueMapItem[] = []) =>
  Object.entries<IOption | IOption[] | null>(data?.picklists || {})
    .map(([fieldId, option]) => {
      const value = Array.isArray(option)
        ? option.map((item) => item?.value).filter(Boolean)
        : option?.value

      const fieldValueId = fieldValueMap.find((item) => item?.fieldId === fieldId)?.fieldValueId

      return fieldValueId ? { fieldValueId, value } : { fieldId, value }
    })
    .filter(({ value }) => (Array.isArray(value) ? value.length : value))
